import { Container, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import { ProcessStepper } from '@components/ProcessStepper/index';
import RecordCard from '@components/RecordCard';

import {
  NoRecordsWrapper,
  Spacer,
  WrapperDiv,
} from '@layouts/Layouts/BigTitleLayout';

import type { MedicationStatementRecord } from '@models/medication';

import {
  getMedicationReportStatusLabel,
  groupMedicationsForReport,
} from '@utils/medication-report';

const messages = defineMessages({
  activeMedications: {
    id: 'medicationsShare.activeMedications',
    defaultMessage: 'Active Medications',
  },
  pastMedications: {
    id: 'medicationsShare.pastMedications',
    defaultMessage: 'Past Medications',
  },
  noActiveMedications: {
    id: 'medicationsShare.noActiveMedications',
    defaultMessage: 'No active medications listed',
  },
  noPastMedications: {
    id: 'medicationsShare.noPastMedications',
    defaultMessage: 'No past medications listed',
  },
});

interface SharedMedicationListProps {
  medications: MedicationStatementRecord[];
  loading: boolean;
}

/**
 * Read-only medication list for the public share-link view. Deliberately has
 * no onClick/navigation and no add/edit controls — those only make sense for
 * an authenticated owner, not an anonymous recipient of a shared link.
 */
const SharedMedicationList: React.FC<SharedMedicationListProps> = ({
  medications,
  loading,
}) => {
  const { formatMessage } = useIntl();
  const { palette } = useTheme();
  const { active, past } = groupMedicationsForReport(medications);

  const renderSection = (
    meds: MedicationStatementRecord[],
    title: string,
    emptyText: string
  ) => (
    <>
      <Typography color="grey.300" sx={{ mt: 3, width: '100%' }} variant="h6">
        {title}
      </Typography>
      {meds.length === 0 ? (
        <NoRecordsWrapper>{emptyText}</NoRecordsWrapper>
      ) : (
        <ProcessStepper
          allBlack
          stepNames={meds.map((m) => (
            <RecordCard
              bottomText={[m.dosageSummary, getMedicationReportStatusLabel(m)]
                .filter(Boolean)
                .join(' · ')}
              clickable={false}
              color={
                m.acquisitionType === 'prescription'
                  ? palette.purple.main
                  : palette.teal.main
              }
              coloredSubtext
              key={m.id}
              noteDisplay={Boolean(m.recordNotes.length)}
              text={m.medication}
              topText={m.acquisitionTypeName}
            />
          ))}
        />
      )}
    </>
  );

  return (
    <ContentLoader loading={loading}>
      <Container maxWidth="sm">
        <WrapperDiv>
          {renderSection(
            active,
            formatMessage(messages.activeMedications),
            formatMessage(messages.noActiveMedications)
          )}
          <Spacer />
          {renderSection(
            past,
            formatMessage(messages.pastMedications),
            formatMessage(messages.noPastMedications)
          )}
        </WrapperDiv>
      </Container>
    </ContentLoader>
  );
};

export default withErrorBoundary(SharedMedicationList, {
  FallbackComponent: ErrorBoundaryFallback,
});
